import {Component, HostListener, Input, OnInit, OnChanges, SimpleChanges} from '@angular/core';
import { ConvertDateUtcTimezoneService } from '@shared/services/convert-date-utc-timezone.service';
import { Ticket } from '../../models/tickets';
import { AppUtilService } from '@shared/services/app-util.service';
import { DropdownToggleService } from '@shared/services/dropdown-toggle.service';
import { TicketsStatusChange } from '../../models/ticket-status-change';
import { TicketLabor } from '../../models/ticket-labor';
import { MatDialog } from '@angular/material/dialog';
import { TicketScheduleComponent } from '../ticket-schedule/ticket-schedule.component';
import { TicketsService } from '../../services/tickets.service';
import { AddTeamMemberAndHourComponent } from '../add-team-member-and-hour/add-team-member-and-hour.component';
import { CommonModule } from '@angular/common';
import { AvatarComponent } from "@shared/components/avatar/avatar.component";
import { MatTooltip } from '@angular/material/tooltip';
import { TeamHoursPopupComponent } from '../team-hours-popup/team-hours-popup.component';
import { AuthService } from '@core/services/auth.service';
import { AppPermissions } from '@core/app.config';
import { Site } from '../../models/tkt-site';
import { TktSiteService } from '../../services/tkt-site.service';
import { SiteType } from '@core/enums/tktSite-enum';

@Component({
  selector: 'app-card-item',
  imports: [CommonModule, AvatarComponent, MatTooltip],
  templateUrl: './card-item.component.html',
  styleUrl: './card-item.component.css',
})
export class CardItemComponent implements OnInit, OnChanges {
  @Input() card: Ticket;
  @Input() showStatus = true;
  @Input() showFooter = true;
  @Input() isFlag = false;
  @Input() isBadgeFlag = false;
  @Input() isBadgeFlagBottom = false;
  @Input() isMenuIcon = false;
  @Input() MenuItemPreFix = '';

  timeZone = '';
  locationLoaded= false;
  siteList: Site[] = [];
  ticketLocationsMap: Record<string, { location: Site; areas: Site[] }[]> = {};
  //
  changeWorkerRead;
  changeWorkerWrite;
  //
  recurringTicketRead;
  recurringTicketWrite;
  //
  changeTicketStatusRead;
  changeTicketStatusWrite;
  //
  teamHoursRead;
  teamHoursWrite;
  //
  ViewAllTicketsRead ;
    ViewAllTicketsWrite ;
    //
  constructor(
    public utcToTimeZone: ConvertDateUtcTimezoneService,
    protected dropdownService: DropdownToggleService,
    private dialog: MatDialog,
    public ticketService: TicketsService,
    public appUtilService: AppUtilService,
    private authService: AuthService,
    private siteService: TktSiteService
  ) {
    const timeZone = localStorage.getItem('timeZones');
    if (timeZone) {
      const parsedTimeZone = JSON.parse(timeZone); // Parse if stored as a JSON string
      this.timeZone = parsedTimeZone[0]; // Use the first time zone (or adjust as needed)
    }
  }
// In your component
trackByLocationId(index: number, loc: any): any {
  return loc.location.id || index;
}
  async ngOnInit() {
    await this.userRights();
    await this.initializeLocations();
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['card'] && this.card) {
      await this.initializeLocations();
    }
  }

  async initializeLocations() {
    this.locationLoaded= false;

    if (!this.card?.ticketLocationMap?.length) {
      this.ticketLocationsMap = {};
      return;
    }

    // Get unique location IDs
    const locationIds = [
      ...new Set(
        this.card.ticketLocationMap.map((lm) => lm.locationId)
      ),
    ];

    if (locationIds.length > 0) {
      // Fetch locations
      const locations = await this.siteService.getSitesByIds(
        locationIds,
        SiteType.LOCATION
      );
      this.siteList = locations;

      // Build ticketLocationsMap
      this.ticketLocationsMap[this.card.id] =
        this.getTicketLocationsWithAreas(this.card);

      this.locationLoaded= true;
    }
  }

  getTagStyle(index: number): string {
    const i = index % 3; // ensures 0,1,2
    switch (i) {
      case 0:
        return 'bg-[#FEFCE8] border border-[#FEF9C2]';
      case 1:
        return 'bg-[#FEF2F2] border border-[#FFE2E2]';
      case 2:
      default:
        return 'bg-[#F0FDF4] border border-[#B9F8CF]';
    }
  }

  ticketLabor(ticket: Ticket): TicketLabor[] {
    const distinctLabors = ticket.ticketLabor.reduce((acc, labor) => {
      if (!acc.some((item) => item.workerId === labor.workerId)) {
        acc.push(labor);
      }
      return acc;
    }, []);
    return distinctLabors;
  }

  ticketLaborUserList(ticket: Ticket): { displayName: string; pic?: string }[] {
    const distinctUsers: { displayName: string; pic?: string }[] = [];

    const seen = new Set<string>();

    for (const labor of ticket.ticketLabor ?? []) {
      if (labor?.workerId && !seen.has(labor.workerId) && labor.workerName) {
        seen.add(labor.workerId);
        distinctUsers.push({
          displayName: labor.workerName,
          pic: labor.workerPic ?? undefined,
        });
      }
    }
    return distinctUsers;
  }


  getSortedTicketLocations(cardId: string) {
    const locations = this.ticketLocationsMap[cardId] || [];

    // 1️⃣ Sort locations by location name (alpha-numeric)
    const sortedLocationNames = this.sortAreasByAlphaNumeric(
      locations.map((l) => l.location.name)
    );
    const sortedLocations = sortedLocationNames
      .map((name) => locations.find((l) => l.location.name === name))
      .filter((l): l is { location: Site; areas: Site[] } => !!l);

    // 2️⃣ Areas are already sorted in getTicketLocationsWithAreas; ensure order here too
    return sortedLocations.map((loc) => ({
      ...loc,
      areas: loc.areas
        ? this.sortAreasByAlphaNumeric(loc.areas.map((a) => a.name)).map(
            (name) => loc.areas!.find((a) => a.name === name)!
          )
        : [],
    }));
  }


  //Apply sorting on it
  private sortAreasByAlphaNumeric(names: string[]): string[] {
      return [...names].sort((a, b) => {
        // Extract letters
        const lettersA = a.match(/[a-zA-Z]+/)?.[0].toLowerCase() || '';
        const lettersB = b.match(/[a-zA-Z]+/)?.[0].toLowerCase() || '';

        // 1️⃣ Sort by letters
        if (lettersA !== lettersB) {
          return lettersA.localeCompare(lettersB);
        }

        // Extract numbers
        const numberA = parseInt(a.match(/\d+/)?.[0] || '0', 10);
        const numberB = parseInt(b.match(/\d+/)?.[0] || '0', 10);

        // 2️⃣ Sort by numbers
        return numberA - numberB;
      });
    }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getFirstTwoAreaNames(loc: any): string {
    if (!loc.areas?.length) return '';
    // Areas are already sorted in getTicketLocationsWithAreas
    const visible = loc.areas.slice(0, 1).map((a: any) => a.name);
    return visible.join(', ');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getRemainingAreasTooltip(loc: any): string {
    if (loc.areas?.length <= 1) return '';

    // Areas are already sorted in getTicketLocationsWithAreas
    const remaining = loc.areas.slice(1);
    const areas = remaining
      .map((a) => {
        const areaNames = a.name;
        return `${areaNames}`;
      })
      .join('\n , '); // newline → handled by .multiline-tooltip
    return areas;
  }


  getTicketLocationsWithAreas(
    ticket: Ticket
  ): { location: Site; areas: Site[] }[] {
    if (!ticket.ticketLocationMap) return [];

    const result = ticket.ticketLocationMap
      .map((lm) => {
        const location = this.siteList.find((s) => s.id === lm.locationId);
        if (!location) return null;

        // filter only the child areas that belong to this ticket
        const selectedAreaIds =
          ticket.ticketAreaMap?.map((am) => am.areaId) || [];
        const selectedAreas = location.childSite.filter((c) =>
          selectedAreaIds.includes(c.id)
        );

        // Sort areas using alpha-numeric logic (letters first, then numbers)
        const sortedAreaNames = this.sortAreasByAlphaNumeric(
          selectedAreas.map((a) => a.name)
        );
        const sortedAreas = sortedAreaNames
          .map((name) => selectedAreas.find((a) => a.name === name))
          .filter((a): a is Site => !!a);

        return { location, areas: sortedAreas };
      })
      .filter((x): x is { location: Site; areas: Site[] } => !!x);

    // Sort locations by name using the same alpha-numeric logic
    const sortedLocationNames = this.sortAreasByAlphaNumeric(
      result.map((r) => r.location.name)
    );
    return sortedLocationNames
      .map((name) => result.find((r) => r.location.name === name))
      .filter((r): r is { location: Site; areas: Site[] } => !!r);
  }

  getRemainingLocationsTooltip(ticket: Ticket): string {
    const all = this.getTicketLocationsWithAreas(ticket);
    if (all.length <= 1) return '';

    const remaining = all.slice(1);
    return remaining
      .map((loc) => {
        const areaNames = loc.areas.map((a) => a.name).join(', ');
        return `${loc.location.name} [ ${areaNames} ]`;
      })
      .join('\n'); // newline → handled by .multiline-tooltip
  }


  getTotalEstimatedHours(ticket: Ticket): number {
    if (!ticket?.ticketLabor || !Array.isArray(ticket.ticketLabor)) return 0;

    return ticket.ticketLabor.reduce((acc: number, labor) => {
      return acc + (labor.estimated || 0);
    }, 0);
  }

  getTotalCost(ticket: Ticket): number {
    if (!ticket?.ticketLabor || !Array.isArray(ticket.ticketLabor)) return 0;

    return ticket.ticketLabor.reduce((acc: number, labor) => {
      return acc + (labor.rate || 0);
    }, 0);
  }

  // Toggle a specific dropdown
  toggleDropdown(id: string, event: MouseEvent): void {
    event.stopPropagation();
    this.dropdownService.toggleDropdown(id);
  }

  // Check if a dropdown is visible
  isDropdownVisible(id: string): boolean {
    return this.dropdownService.isDropdownVisible(id);
  }

  // Hide all dropdowns when clicking anywhere else in the document
  @HostListener('document:click')
  onDocumentClick(): void {
    this.dropdownService.hideAllDropdowns();
  }

  onDivClick(event: MouseEvent): void {
    event.stopPropagation(); // Prevent document click listener from closing dropdown
  }

  getLatestChangeStatus(ticketChange: Ticket): TicketsStatusChange | undefined {
    if (
      ticketChange?.ticketStatusChanges &&
      ticketChange.ticketStatusChanges.length > 0
    ) {
      const latestChange = ticketChange.ticketStatusChanges.reduce(
        (latest, change) => {
          return new Date(latest.changeOn) > new Date(change.changeOn)
            ? latest
            : change;
        },
        ticketChange.ticketStatusChanges[0] // initial value
      );
      return latestChange;
    }
    return undefined;
  }

  ticketSchedule(ticketId): void {
    this.dropdownService.hideAllDropdowns();
    this.ticketService.selectedTicketId = ticketId;
    const width = this.SetDialogWidth();
    this.dialog.open(TicketScheduleComponent, {
      minWidth: width,
      width: width,
      // minHeight: '545px',
      // maxHeight: '545px',
      panelClass: 'overflow_visible',
      hasBackdrop: true,
      disableClose: true,
    });
  }

  // updateTicket(event:any){
  //   this.isChangeAnyThing=1;
  //   // this.ngOnInit('network-only');
  // }

  //     callChild() {
  //   if (this.teamHourComponent) {
  //       this.teamHourComponent.ngOnInit();
  //     }
  //     if (this.addTeamHourComponent) {
  //       this.addTeamHourComponent.getData();
  //     }
  // }

  AddTeamMemberAndHour(ticket): void {
    this.dropdownService.hideAllDropdowns();
    const width = this.SetDialogWidthTeamMember();
    this.ticketService.selectedTicketId = ticket.id;
    this.ticketService.ticketLaborList = ticket.ticketLabor;
    const dialog = this.dialog.open(AddTeamMemberAndHourComponent, {
      minWidth: width,
      width: width,
      // minHeight: '545px',
      // maxHeight: '545px',
      panelClass: 'overflow_visible',
      hasBackdrop: true,
      disableClose: true,
      data: {
        estimatedHoursOnly: true,
      },
    });
    dialog.afterClosed().subscribe(async (result) => {
      if (!result) return;

      if (result) {
        this.getTotalEstimatedHours(ticket);
      }
    });
  }

  getDialogDimensionsTicketDetailPopup(): {
    maxHeight: string;
    width: string;
    height: string;
  } {
    const windowWidth = window.innerWidth;

    if (windowWidth < 1030) {
      return { width: '94vw', height: '502px', maxHeight: '98vh' };
    } else {
      return { width: '876px', height: '502px', maxHeight: '98vh' };
    }
  }

  openTeamLogHours(ticket: Ticket) {
    this.dropdownService.hideAllDropdowns();
    const { width, height, maxHeight } =
      this.getDialogDimensionsTicketDetailPopup();
    this.dialog.open(TeamHoursPopupComponent, {
      minWidth: width,
      width: width,
      height: height,
      maxHeight: maxHeight,
      panelClass: 'center-popup',
      hasBackdrop: true,
      disableClose: true,
      data: {
        ticketId: ticket?.id,
        ticket: ticket,
        isEstimatedTimeOnly: true,
        changeworker: this.changeWorkerWrite,
      },
    });
  }

  private getDialogWidth(): string {
    const windowWidth = window.innerWidth;
    if (windowWidth < 750) {
      return '94vw';
    }
    return '480px';
  }

  SetDialogWidth(): string {
    return this.getDialogWidth();
  }

  SetDialogWidthTeamMember(): string {
    return this.getDialogWidth();
  }

  userRights() {
    this.changeWorkerRead = this.authService.hasPermission(
      AppPermissions.ChangeWorkerRead
    );
    this.changeWorkerWrite = this.authService.hasPermission(
      AppPermissions.ChangeWorkerWrite
    );

    //
    this.recurringTicketRead = this.authService.hasPermission(
      AppPermissions.TicketRecurringJobsRead
    );
    this.recurringTicketWrite = this.authService.hasPermission(
      AppPermissions.TicketRecurringJobsWrite
    );
    //
    this.changeTicketStatusRead = this.authService.hasPermission(
      AppPermissions.ChangeTicketStatusRead
    );
    this.changeTicketStatusWrite = this.authService.hasPermission(
      AppPermissions.ChangeTicketStatusWrite
    );
    //
    this.teamHoursRead = this.authService.hasPermission(
      AppPermissions.TeamHoursRead
    );
    this.teamHoursWrite = this.authService.hasPermission(
      AppPermissions.TeamHoursWrite
    );
    //
    this.ViewAllTicketsRead = this.authService.hasPermission(AppPermissions.ViewAllTicketsRead);
      this.ViewAllTicketsWrite = this.authService.hasPermission(AppPermissions.ViewAllTicketsWrite);
      //
  }
}
